import React, { useState, useEffect } from 'react';
import { Container, Form, Row, Col, Button, Alert, Spinner, Card } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useInterfaceStore } from '../../stores/useInterfaceStore';
import { notify } from '../../stores/useNotificationStore';

interface IDiretoria {
  diretoria: string;
  nome_diretor: string;
  email: string;
  telefone: string;
}

interface IGerencia {
  gerencia: string;
  nome_gerente: string;
  email: string;
  telefone: string;
}

interface IArea {
  nome: string;
}

const initialDiretoria: IDiretoria = {
  diretoria: '',
  nome_diretor: '',
  email: '',
  telefone: '',
};

const initialGerencia: IGerencia = {
  gerencia: '',
  nome_gerente: '',
  email: '',
  telefone: '',
};

const formatPhone = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 10) {
    return digits
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d{1,4})$/, '$1-$2');
  }
  return digits
    .replace(/^(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d{1,4})$/, '$1-$2');
};

const isValidEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const EstruturaOrganizacional: React.FC = () => {
  const [nome, setNome] = useState('');
  const [diretoria, setDiretoria] = useState<IDiretoria>({ ...initialDiretoria });
  const [gerencias, setGerencias] = useState<IGerencia[]>([{ ...initialGerencia }]);
  const [areas, setAreas] = useState<IArea[]>([{ nome: '' }]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { empresaId } = useInterfaceStore();

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setIsLoading(true);
      try {
        const response = await api.getEstruturaOrganizacional(parseInt(id));
        if (response.success && response.data) {
          const d = response.data as any;
          setNome(d.nome || '');
          if (d.diretoria) setDiretoria(d.diretoria);
          if (d.gerencias?.length) setGerencias(d.gerencias);
          if (d.areas?.length) setAreas(d.areas);
        } else {
          setError('Estrutura não encontrada.');
          navigate('/app/estrutura-organizacional/list');
        }
      } catch {
        setError('Erro ao carregar dados.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [id, navigate]);

  const handleDiretoriaChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setDiretoria(prev => ({
      ...prev,
      [name]: name === 'telefone' ? formatPhone(value) : value,
    }));
  };

  const handleGerenciaChange = (index: number, e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setGerencias(prev =>
      prev.map((g, i) =>
        i === index ? { ...g, [name]: name === 'telefone' ? formatPhone(value) : value } : g
      )
    );
  };

  const addGerencia = () => setGerencias(prev => [...prev, { ...initialGerencia }]);

  const removeGerencia = (index: number) => {
    if (gerencias.length <= 1) return;
    setGerencias(prev => prev.filter((_, i) => i !== index));
  };

  const handleAreaChange = (index: number, value: string) => {
    setAreas(prev => prev.map((a, i) => (i === index ? { nome: value } : a)));
  };

  const addArea = () => setAreas(prev => [...prev, { nome: '' }]);

  const removeArea = (index: number) => {
    if (areas.length <= 1) return;
    setAreas(prev => prev.filter((_, i) => i !== index));
  };

  const validate = (): boolean => {
    if (!nome.trim()) { setError('Nome é obrigatório.'); return false; }
    if (!empresaId) { setError('Nenhuma empresa selecionada.'); return false; }
    if (diretoria.email && !isValidEmail(diretoria.email)) {
      setError('E-mail da diretoria inválido.'); return false;
    }
    for (let i = 0; i < gerencias.length; i++) {
      if (gerencias[i].email && !isValidEmail(gerencias[i].email)) {
        setError(`E-mail da gerência ${i + 1} inválido.`); return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    setError('');
    if (!validate()) return;

    setIsLoading(true);
    try {
      const payload = { empresa_id: empresaId, nome, diretoria, gerencias, areas };
      const response = id
        ? await api.updateEstruturaOrganizacional(parseInt(id), payload)
        : await api.createEstruturaOrganizacional(payload);

      if (response.success) {
        notify.success(id ? 'Estrutura atualizada com sucesso!' : 'Estrutura cadastrada com sucesso!');
        navigate('/app/estrutura-organizacional/list');
      } else {
        setError((response.error as string) || 'Erro ao salvar.');
      }
    } catch {
      setError('Erro de conexão ao tentar salvar.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && id) {
    return (
      <Container fluid className="p-4 text-center">
        <Spinner animation="border" /> <span>Carregando...</span>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      <h1 className="mb-4">
        {id ? 'Editar Estrutura Organizacional' : 'Cadastro de Estrutura Organizacional'}
      </h1>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      <Form>
        <Row className="g-3 mb-4">
          <Col md={12}>
            <Form.Group controlId="nome">
              <Form.Label>Nome *</Form.Label>
              <Form.Control
                type="text"
                value={nome}
                onChange={e => setNome(e.target.value)}
                placeholder="Ex: Tecnologia da Informação"
              />
            </Form.Group>
          </Col>
        </Row>

        {/* Diretoria */}
        <Card className="mb-4">
          <Card.Header>
            <strong>Diretoria</strong>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Diretoria</Form.Label>
                  <Form.Control
                    type="text"
                    name="diretoria"
                    value={diretoria.diretoria}
                    onChange={handleDiretoriaChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Nome Diretor</Form.Label>
                  <Form.Control
                    type="text"
                    name="nome_diretor"
                    value={diretoria.nome_diretor}
                    onChange={handleDiretoriaChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>E-mail</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={diretoria.email}
                    onChange={handleDiretoriaChange}
                    isInvalid={!!diretoria.email && !isValidEmail(diretoria.email)}
                  />
                  <Form.Control.Feedback type="invalid">E-mail inválido.</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Telefone</Form.Label>
                  <Form.Control
                    type="tel"
                    name="telefone"
                    value={diretoria.telefone}
                    onChange={handleDiretoriaChange}
                    placeholder="(00) 00000-0000"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Gerências */}
        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <strong>Gerências</strong>
            <Button variant="outline-primary" size="sm" onClick={addGerencia}>
              + Adicionar Gerência
            </Button>
          </Card.Header>
          <Card.Body>
            {gerencias.map((g, index) => (
              <div key={index} className={index > 0 ? 'mt-3 pt-3 border-top' : ''}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted small fw-semibold">Gerência {index + 1}</span>
                  {gerencias.length > 1 && (
                    <Button variant="outline-danger" size="sm" onClick={() => removeGerencia(index)}>
                      Remover
                    </Button>
                  )}
                </div>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Gerência</Form.Label>
                      <Form.Control
                        type="text"
                        name="gerencia"
                        value={g.gerencia}
                        onChange={e => handleGerenciaChange(index, e)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Nome Gerente</Form.Label>
                      <Form.Control
                        type="text"
                        name="nome_gerente"
                        value={g.nome_gerente}
                        onChange={e => handleGerenciaChange(index, e)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>E-mail</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={g.email}
                        onChange={e => handleGerenciaChange(index, e)}
                        isInvalid={!!g.email && !isValidEmail(g.email)}
                      />
                      <Form.Control.Feedback type="invalid">E-mail inválido.</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Telefone</Form.Label>
                      <Form.Control
                        type="tel"
                        name="telefone"
                        value={g.telefone}
                        onChange={e => handleGerenciaChange(index, e)}
                        placeholder="(00) 00000-0000"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            ))}
          </Card.Body>
        </Card>

        {/* Áreas / Departamentos */}
        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <strong>Áreas ou Departamentos</strong>
            <Button variant="outline-primary" size="sm" onClick={addArea}>
              + Adicionar Área
            </Button>
          </Card.Header>
          <Card.Body>
            {areas.map((a, index) => (
              <div
                key={index}
                className={`d-flex align-items-center gap-2${index > 0 ? ' mt-2' : ''}`}
              >
                <Form.Control
                  type="text"
                  value={a.nome}
                  onChange={e => handleAreaChange(index, e.target.value)}
                  placeholder="Nome da área ou departamento"
                />
                {areas.length > 1 && (
                  <Button variant="outline-danger" size="sm" onClick={() => removeArea(index)}>
                    Remover
                  </Button>
                )}
              </div>
            ))}
          </Card.Body>
        </Card>

        <div className="text-end mt-2 mb-4">
          <Button
            variant="secondary"
            className="me-2"
            onClick={() => navigate('/app/estrutura-organizacional/list')}
          >
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? <Spinner as="span" animation="border" size="sm" /> : 'Salvar'}
          </Button>
        </div>
      </Form>
    </Container>
  );
};

export default EstruturaOrganizacional;
